import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import programArtMotif from "@/assets/images/user/programArtMotif.png";
import programArtTab from "@/assets/images/user/programArtTab.svg";
import programArtTabM from "@/assets/images/user/programArtTabM.svg";
import programArtTabT from "@/assets/images/user/programArtTabT.svg";
import programFestaMotif from "@/assets/images/user/programFestaMotif.png";
import programFestaTab from "@/assets/images/user/programFestaTab.svg";
import programFestaTabM from "@/assets/images/user/programFestaTabM.svg";
import programFestaTabT from "@/assets/images/user/programFestaTabT.svg";
import programFocusMotif from "@/assets/images/user/programFocusMotif.png";
import programFocusTab from "@/assets/images/user/programFocusTab.svg";
import programFocusTabM from "@/assets/images/user/programFocusTabM.svg";
import programFocusTabT from "@/assets/images/user/programFocusTabT.svg";
import programPlayMotif from "@/assets/images/user/programPlayMotif.png";
import programPlayTab from "@/assets/images/user/programPlayTab.svg";
import programPlayTabM from "@/assets/images/user/programPlayTabM.svg";
import programPlayTabT from "@/assets/images/user/programPlayTabT.svg";
import programStageMap from "@/assets/images/user/programStageMap-1.jpg";
import programPlayMap from "@/assets/images/user/programStageMap-2.jpg";
import programArtMap from "@/assets/images/user/programStageMap-3.jpg";
import programFocusMap from "@/assets/images/user/programStageMap-4.jpg";
// FESTA 안내도만 2026 신규 지도로 교체. 표시 폭(1400 - padding 60*2 = 1280)의 2배수 PNG.
// 지도는 평면 색·글자 위주라 JPEG 링잉을 피하려고 무손실 PNG 를 쓴다.
import programFestaMap from "@/assets/images/user/programFesta2026Map.png";
import programStageMotif from "@/assets/images/user/programStageMotif.png";
import programStageTab from "@/assets/images/user/programStageTab.svg";
import programStageTabM from "@/assets/images/user/programStageTabM.svg";
import programStageTabT from "@/assets/images/user/programStageTabT.svg";
// import ComingSoon from "@/components/user/common/ComingSoon";
import TitleBox from "@/components/user/common/TitleBox";
import ImgZoomPop from "@/components/user/popup/ImgZoomPop";

// 반응형 커버를 위해 통 이미지 대신 [배경 그라디언트 + 좌상단 모티프 PNG]로 분리
const tabList = [
    {
        title: "stage",
        label: "THE STAGE",
        link: "/program/stage",
        gradient: "linear-gradient(90deg, #E9492A 0%, #FF7F71 100%)",
        motif: programStageMotif,
        mobileImg: programStageTabM,
        tabletImg: programStageTabT,
        desktopImg: programStageTab,
        map: programStageMap,
        mapBorder: "#E9492A",
    },
    {
        title: "play",
        label: "THE PLAY",
        link: "/program/play",
        gradient: "linear-gradient(90deg, #00B1AA 0%, #44DDD7 100%)",
        motif: programPlayMotif,
        mobileImg: programPlayTabM,
        tabletImg: programPlayTabT,
        desktopImg: programPlayTab,
        map: programPlayMap,
        mapBorder: "#00B1AA",
    },
    {
        title: "art",
        label: "THE ART",
        link: "/program/art",
        gradient: "linear-gradient(90deg, #D02F8B 0%, #F46AB9 100%)",
        motif: programArtMotif,
        mobileImg: programArtTabM,
        tabletImg: programArtTabT,
        desktopImg: programArtTab,
        map: programArtMap,
        mapBorder: "#D02F8B",
    },
    {
        title: "focus",
        label: "THE FOCUS",
        link: "/program/focus",
        gradient: "linear-gradient(90deg, #FABF00 0%, #FFDA64 100%)",
        motif: programFocusMotif,
        mobileImg: programFocusTabM,
        tabletImg: programFocusTabT,
        desktopImg: programFocusTab,
        map: programFocusMap,
        mapBorder: "#FABF00",
    },
    {
        title: "festa",
        label: "THE FESTA",
        link: "/program/festa",
        gradient: "linear-gradient(90deg, #006CB8 0%, #349CE5 100%)",
        motif: programFestaMotif,
        mobileImg: programFestaTabM,
        tabletImg: programFestaTabT,
        desktopImg: programFestaTab,
        map: programFestaMap,
        mapBorder: "#006CB8",
    },
];

interface ProgramLayoutProps {
    children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function ProgramLayout({ children }: ProgramLayoutProps) {
    const location = useLocation();
    const [tabOn, setTabOn] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const prevPathRef = useRef<string | null>(null);
    const activeTab = tabList[tabOn] ?? tabList[0];

    useEffect(() => {
        if (location.pathname === "/program/stage") {
            setTabOn(0);
        } else if (location.pathname === "/program/play") {
            setTabOn(1);
        } else if (location.pathname === "/program/art") {
            setTabOn(2);
        } else if (location.pathname === "/program/focus") {
            setTabOn(3);
        } else if (location.pathname === "/program/festa") {
            setTabOn(4);
        }
    }, [location.pathname]);

    // 프로그램 하위 탭 간 이동 시 최상단(서브비주얼)이 아닌 프로그램 섹션 위치로 스크롤
    // (App의 window.scrollTo(0,0) 이후 실행되도록 rAF 사용, 첫 진입은 상단 스크롤 유지)
    useEffect(() => {
        const prev = prevPathRef.current;
        const isTabSwitch =
            !!prev &&
            prev !== location.pathname &&
            prev.startsWith("/program") &&
            location.pathname.startsWith("/program");
        if (isTabSwitch) {
            requestAnimationFrame(() => rootRef.current?.scrollIntoView({ block: "start" }));
        }
        prevPathRef.current = location.pathname;
    }, [location.pathname]);

    return (
        <div
            ref={rootRef}
            className="mx-auto w-full max-w-[1400px] scroll-mt-[80px] p-[0_20px_120px] md:scroll-mt-[100px] md:pb-[160px] xl:scroll-mt-[130px] xl:px-0"
        >
            <div className="flex flex-col gap-[28px] md:gap-[48px]">
                <TitleBox title="PROGRAM" text="프로그램" />
                <div className="-ml-[20px] w-[calc(100%+40px)] xl:ml-0 xl:w-full">
                    {/* overflow-hidden: 탭 아래로 새는 그림자 클리핑 */}
                    <ul className="flex h-[100px] overflow-hidden">
                        {tabList.map((tab, index) => (
                            <li
                                key={`tab_${index}`}
                                className={`h-full w-1/5 overflow-hidden rounded-[8px_8px_0_0] ${tabOn === index ? "relative z-[1] opacity-100 shadow-[4px_4px_8px_0px_#00000040]" : "opacity-60"}`}
                            >
                                <Link
                                    to={tab.link}
                                    className="relative flex h-full items-center justify-center"
                                    style={{ background: tab.gradient }}
                                >
                                    {/* 좌상단 모티프: 세부 위치는 left/top으로 조정 */}
                                    <img
                                        src={tab.motif}
                                        alt=""
                                        aria-hidden
                                        className="pointer-events-none absolute left-0 top-0"
                                    />
                                    <img src={tab.mobileImg} alt={tab.title} className="relative md:hidden" />
                                    <img
                                        src={tab.tabletImg}
                                        alt={tab.title}
                                        className="relative hidden md:block xl:hidden"
                                    />
                                    <img src={tab.desktopImg} alt={tab.title} className="relative hidden xl:block" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                    {/* <ComingSoon /> */}
                    {/* 행사장 안내도: 탭 컬러 테두리 + 하단만 라운드(시안 1400 기준 padding 60) */}
                    <div
                        className="bg-white p-[20px_0_0] md:rounded-[0_0_32px_32px] md:border-8 md:p-[28px] xl:p-[60px]"
                        style={{ borderColor: activeTab.mapBorder }}
                    >
                        <ImgZoomPop img={activeTab.map} alt={`${activeTab.label} 행사장 안내도`} fit="width" />
                    </div>
                </div>
            </div>
            {children}
        </div>
    );
}
